/**
 * Honeybadger error monitoring client for React frontend
 *
 * This service initializes Honeybadger for client-side error tracking.
 * Errors are reported to the same Honeybadger project as the Rails backend.
 *
 * Usage:
 *   import { honeybadger, notifyError } from '@/services/honeybadger';
 *
 *   // Report an error manually
 *   notifyError(error, { component: 'MyComponent' });
 *
 *   // Use the ErrorBoundary in your app
 *   import { HoneybadgerErrorBoundary } from '@/services/honeybadger';
 */

import Honeybadger from '@honeybadger-io/js';

// Get configuration from environment or meta tags
function getConfig() {
  // Check for meta tags (set by Rails in the layout)
  const apiKeyMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="honeybadger-api-key"]'
  );
  const envMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="honeybadger-environment"]'
  );
  const revisionMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="honeybadger-revision"]'
  );

  return {
    apiKey: apiKeyMeta?.content || import.meta.env.VITE_HONEYBADGER_API_KEY || '',
    environment: envMeta?.content || import.meta.env.MODE || 'development',
    revision: revisionMeta?.content || import.meta.env.VITE_GIT_SHA || '',
  };
}

// Initialize Honeybadger
const config = getConfig();

const honeybadger = Honeybadger.configure({
  apiKey: config.apiKey,
  environment: config.environment,
  revision: config.revision,
  // Don't report errors in development
  reportData: config.environment !== 'development' && config.environment !== 'test',
  // Enable breadcrumbs for better debugging context
  breadcrumbsEnabled: true,
  // Max breadcrumbs to keep
  maxBreadcrumbs: 40,
});

/**
 * Manually report an error to Honeybadger
 *
 * @param error - The error to report
 * @param context - Additional context to include with the error
 */
export function notifyError(
  error: Error | string,
  context?: Record<string, unknown>
): void {
  honeybadger.notify(error, { context });
}

/**
 * Set user context for error reports
 * Call this after a user logs in
 *
 * @param user - User information to attach to errors
 */
export function setUserContext(user: {
  id?: string;
  email?: string;
  name?: string;
}): void {
  honeybadger.setContext({
    user_id: user.id,
    user_email: user.email,
    user_name: user.name,
  });
}

/**
 * Clear user context
 * Call this when a user logs out
 */
export function clearUserContext(): void {
  honeybadger.resetContext();
}

/**
 * Add a breadcrumb for debugging context
 *
 * @param message - Description of the event
 * @param metadata - Additional data about the event
 */
export function addBreadcrumb(
  message: string,
  metadata?: Record<string, unknown>
): void {
  honeybadger.addBreadcrumb(message, { metadata });
}

// Export the configured Honeybadger instance
export { honeybadger };

// Re-export the React error boundary
export { Honeybadger as HoneybadgerCore };
